import {
  CampaignsApiException,
  ProposalCampaignConversionErrorCode as Code,
} from '@urbanx/agx.campaigns.client';

// The next step the dialog offers. Retrying is safe after any outcome: the API
// returns the existing agreement instead of creating a second draft.
export type ConversionNextStep = 'reviewAgain' | 'retry' | 'openExisting' | 'none';

export type ConversionFailure = {
  code?: Code;
  message: string;
  next: ConversionNextStep;
  // The proposal form that created the existing agreement, for opening it.
  sourceFormId?: string;
};

const failures: Record<Code, { message: string; next: ConversionNextStep }> = {
  InvalidInput: {
    message: 'The agreement draft request was incomplete. Review the proposal again.',
    next: 'reviewAgain',
  },
  UnsupportedState: {
    message: 'Agreement drafts can only be created for NSW and QLD proposals.',
    next: 'none',
  },
  InvalidFormType: {
    message: 'The reviewed proposal draft is no longer available. Review the proposal again.',
    next: 'reviewAgain',
  },
  FormNotFound: {
    message: 'The reviewed proposal draft is no longer available. Review the proposal again.',
    next: 'reviewAgain',
  },
  CampaignNotFound: {
    message: 'You can’t create an agreement draft for this campaign.',
    next: 'none',
  },
  AccessDenied: {
    message: 'You can’t create an agreement draft for this campaign.',
    next: 'none',
  },
  FeatureDisabled: {
    message: 'Creating agreement drafts is currently unavailable.',
    next: 'none',
  },
  CampaignIneligible: {
    message: 'This campaign can no longer be converted to an agreement.',
    next: 'none',
  },
  ReviewConflict: {
    message:
      'This campaign changed after you reviewed it. Review it again before creating the agreement draft.',
    next: 'reviewAgain',
  },
  // Only the next proposal save finishes a pending marketing change; reviewing again cannot.
  MarketingPending: {
    message:
      'A marketing change on this proposal didn’t finish saving. Save the proposal’s marketing details again before creating the agreement draft.',
    next: 'none',
  },
  AgreementStateConflict: {
    message:
      'This campaign already has an agreement that wasn’t created from this proposal. Continue from the campaign on the dashboard.',
    next: 'none',
  },
  SourceFormConflict: {
    message: 'This campaign’s agreement draft was created from another proposal draft.',
    next: 'openExisting',
  },
  PublicationPending: {
    message: 'The agreement draft is saved, but its setup hasn’t finished. Retry to finish it.',
    next: 'retry',
  },
  PublicationFailed: {
    message:
      'The agreement draft is saved, but its setup couldn’t finish. Contact support before continuing.',
    next: 'none',
  },
};

export const classifyConversionError = (error: unknown): ConversionFailure => {
  // Declared JSON failures are thrown as the response body by the generated client.
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    const code = Object.values(Code).find((code) => code === error.code);
    if (code) {
      const sourceFormId =
        'sourceFormId' in error && typeof error.sourceFormId === 'string'
          ? error.sourceFormId
          : undefined;
      if (code === Code.SourceFormConflict && !sourceFormId)
        return { code, message: failures[code].message, next: 'none' };
      return { code, ...failures[code], sourceFormId };
    }
  }
  // Every declared status throws its body, so only a 401 throws the client's exception. A
  // missing or non-JSON body (an unhandled server failure, a middleware 403 or a gateway) says
  // nothing about the request, and retrying is safe.
  if (error instanceof CampaignsApiException && error.status === 401)
    return {
      message: 'Your session could not be verified. Sign in again to continue.',
      next: 'none',
    };
  return { message: 'Unable to create the agreement draft. Please retry.', next: 'retry' };
};
